import { AssistantKey } from '../../TModules';
import { configStudio } from './configStudio';
import { wizard } from './wizard';

const starters = {
  global: {
    changed: 'What changed?',
    attention: 'What needs my attention?',
    why: 'Why is this happening?',
    trend: 'Show me the trend',
    report: 'Generate a detailed report',
    next: 'What can I do next?'
  },
  customerInsights: {
    trend: 'Show me the engagement trend.',
    attention: 'Which segments need attention?',
    report: 'Generate a detailed report on my customer base.'
  },
  customer360: {
    engagement: 'Show this customer’s engagement trend',
    risk: 'Why is this customer At-risk?'
  },
  /*
    One prompt per card FAMILY (`promptStarters.ts` ORBIT_CARD_FAMILIES), offered for the cards the
    reader actually has on screen so the contextual row follows the screenful rather than the route.
    FR §4.3.1 allows a static dashboard-owned catalogue keyed by screen or metric and forbids the
    backend generating any. TODO(AF-5): wording pending product confirmation. PLA-9563 §2a.
  */
  card: {
    lifecycle: 'Which lifecycle stages are growing?',
    engagement: 'Where is engagement dropping off?',
    behaviour: 'What behaviour changed most recently?',
    value: 'Where is customer value concentrated?',
    risk: 'Which risk signals are rising?',
    readiness: 'Who is ready for the next offer?',
    sentiment: 'How is customer sentiment moving?',
    dataQuality: 'How reliable is this data?'
  },
  // Subtitle of each suggested-action row (`4952:9000`). One per starter id, so the row shape the
  // home frame draws is never half-empty. TODO(AF-5): same pending-product-confirmation as the
  // starter copy itself.
  detail: {
    global: {
      changed: 'See what moved since your last visit.',
      attention: 'Run an AI sweep for urgent tasks.',
      why: 'Trace the drivers behind the current numbers.',
      trend: 'Chart the movement over the recent period.',
      report: 'Queue a full report for download.',
      next: 'Get a recommended next action.'
    },
    customerInsights: {
      trend: 'Chart engagement across your customer base.',
      attention: 'Find the segments that are slipping.',
      report: 'Queue a customer-base report for download.'
    },
    customer360: {
      engagement: 'Chart this customer’s recent activity.',
      risk: 'Explain the signals behind the risk score.'
    },
    card: {
      lifecycle: 'Compare the stage counts across the customer base.',
      engagement: 'Trace the step losing the most customers.',
      behaviour: 'Surface the biggest movement in the behaviour scores.',
      value: 'Rank current and predicted customer value.',
      risk: 'List the risk indicators moving against you.',
      readiness: 'Count the customers clearing the readiness gates.',
      sentiment: 'Track sentiment against expectations.',
      dataQuality: 'Check freshness, coverage and identity confidence.'
    }
  }
};

// Home panel `4952:8961`. The greet pair switches on whether the route carries a context Orbit
// recognises: the frame's "Context Detected" headline is a claim, not a salutation.
const home = {
  greetTitle: 'Context Detected',
  greetBody: 'I’m ready to analyze your data.',
  idleTitle: 'Hi, I’m Orbit',
  idleBody: 'Ask a question or pick a suggestion to get started.',
  suggested: 'Suggested Actions',
  recent: 'Recent Sessions',
  viewAll: 'View all history →'
};

// ctx-title `4952:5399` and the Screen chip `4952:5402`. The glyphs are part of the drawn strings,
// as the session bullet is in `notice.activeSession`.
// state-idle `4952:5397`: the panel over a screen Orbit recognises, before the first question. Its
// context header is `context.detected` above, shared with every other panel that shows the chips.
const idle = {
  suggested: 'Suggested Queries'
};

const context = {
  detected: '◎ Context Detected',
  screen: 'Screen: {{screen}}',
  // `5032:12031` / `5032:8070`. The value is the screen's own date-range selection, read from its
  // live filter bar; the drawn Region chip has no filter behind it and is not rendered.
  period: 'Period: {{period}}',
  /*
    Every OTHER filter the screen is applying, one chip each, in the order its own filter bar lists
    them. Both words are supplied by the screen — the category name and the selected option as the
    filter bar itself prints them — so the panel and the page cannot read differently. The frame
    draws only Screen, Region and Period, and it draws them as `glyph Label: Value`; this is that
    shape, parameterised, for the categories the frame never had to name. The glyph is no longer
    part of the string: `OrbitChipIcon` draws it, so the copy carries words alone. PLA-8844.
  */
  chip: '{{label}}: {{value}}',
  /*
    The card the reader has scrolled to. The in-scope chip row draws Screen, Region and Period and no
    card chip; ORB-CI-02 `4592:5432` names a "Selected Card/Metric Context" state but belongs to the
    reference family the user ruled against (`reference-frames.md`). So the wording follows the
    drawn chips' own `glyph Label: Value` shape, with the glyph drawn by `OrbitChipIcon` rather than
    carried in the copy. PLA-8844.
  */
  card: 'Card: {{card}}',
  /*
    Three or four cards are on screen at once, so the row draws one chip each and collapses the
    rest into this count rather than filling the panel with titles (`ORBIT_CARD_CHIP_LIMIT`).
  */
  cardsMore: '+{{count}} more',
  /*
    Keep Current Investigation freezes the conversation on the context it started in, and every
    later question is sent under THAT context. The chip row therefore draws the pinned context, not
    the live screen: drawn live, the row promised a screen the next answer would not be about
    (PLA-9563 §1c). This line says so, and names the live screen the reader can switch to.
  */
  pinnedNote: 'Pinned to this investigation. The screen now shows {{screen}}.',
  pinnedSame: 'Pinned to this investigation.',
  screens: {
    customerInsights: 'Customer Insights',
    customer360: 'Customer 360',
    // Desktop-7's host. Financial Health Insights, the host of Desktop-8 and Desktop-12, has no
    // label here because it has no route: it is a section of Customer 360.
    customerProfile: 'Customer Profile'
  }
};

/*
  div#conflictCard `4952:7658`. v3 draws TWO copy variants of this card: the dashboard-context
  wording (`5032:7993` / `5032:8005`) and a chat-context one (`5032:9698` / `5032:9710`, "The chat
  context has changed" / "Entered message has changed the current investigation context").

  The dashboard wording is the one built. Every conflict this dashboard can raise comes from
  `useOrbitEntryContext` re-reading the route: `contextChanged` in OrbitProvider compares the live
  route's screen and customer against the session's, and nothing else can raise the card. A typed
  message cannot move Orbit's context — the composer sends a question, and the turn it creates
  carries the same context object the route already resolved — so the chat variant has no condition
  to fire on and saying "entered message has changed the context" would be false on every path that
  actually reaches this card. TODO(AF-15): if PLA-8839 ever returns a subject the backend inferred
  from the question, that is the signal the chat variant needs.

  The frame's body names a segment and a date range, which are the EMEA sales demo's own filters;
  Orbit's context is the screen and, on Customer 360, the customer, so the sentence names what
  actually moved.
*/
const conflict = {
  title: 'The dashboard context has changed',
  body: 'You moved the dashboard to a different context mid-investigation. Applying it could change what this investigation means.',
  investigating: 'Investigating',
  nowShows: 'Dashboard now shows',
  subject: '{{subject}} · {{screen}}',
  customer: '{{screen}} · #{{id}}',
  customerName: '{{screen}} — {{name}}',
  unknown: 'Elsewhere in the dashboard',
  // `redesign-plan.md:633-634` is the canonical wording: the decision is about the CONTEXT the
  // investigation runs in, and only the middle button starts a second investigation.
  continueNew: 'Continue with New Context',
  saveAndStart: 'Save Existing and Start New Investigation',
  keepCurrent: 'Keep Current Context',
  // Appended to a compare row when the screen's own filters are part of what moved.
  withFilters: '{{value}} · {{filters}}'
};

// Input `4952:6249` also draws a "Session restricted..." placeholder. PLA-9579 retired it with the
// rest of the Desktop-5 restricted-session frame: a restriction is a property of one turn, and the
// composer stays askable after it.
const composer = {
  placeholder: 'Ask Orbit a question...',
  tooLong: 'Questions are limited to {{max}} characters.'
};

// One label per non-`answered` outcome. `error` and `timeout` carry no answer text from the backend
// (contract §3-4), so those two have to read as complete sentences on their own.
const outcome = {
  clarify: 'Needs clarification',
  unsupported: 'Not supported yet',
  permissionRestricted: 'Permission restricted',
  insufficientData: 'Insufficient data',
  notConfigured: 'Not configured',
  // The one outcome the backend may raise with no answer prose behind it: there is no measurement
  // to describe, so the panel states the situation itself (PLA-9579).
  notConfiguredBody: 'This metric is not set up for your tenant yet.',
  reportQueued: 'Report queued',
  error: 'Orbit couldn’t answer this one. Please try again.',
  /*
    TODO(PLA-9581): wording awaiting product confirmation. PLA-9580 tags a failed turn with the
    service that broke; the generic `error` line above stays the copy for `internal`, for a turn
    that carries no category, and for a category this build does not know.
  */
  errorUpstreamData: 'Data source unavailable',
  errorUpstreamDataBody:
    'The data source Orbit reads from is temporarily unavailable. Please try again shortly.',
  errorUpstreamLlm: 'AI service unavailable',
  errorUpstreamLlmBody:
    'The AI service Orbit uses is temporarily unavailable. Please try again shortly.',
  timeout: 'Orbit didn’t get an answer in time. Please try again.'
};

const answer = {
  scope: 'Scope',
  // The window the metric was computed over, from `planned_window`; its last day is inclusive.
  period: 'Period',
  periodRange: '{{first}} – {{last}}',
  periodUntil: 'Up to {{last}}',
  explanation: 'Explanation',
  confidence: 'Confidence',
  dataStatus: 'Data status',
  restrictions: 'Restrictions',
  // Section label above the suggested-action rows (`4952:8122`); it replaced the "Next step" detail
  // row, which drew the same string with no affordance.
  suggested: 'Suggested Actions',
  // code-box `5032:10506`, Desktop-13. Drawn while the queued report has not reached a terminal
  // state; `OrbitReportStatus` then swaps it for the success or failure card.
  reportGenerating: 'Generating requested report through reporting module...',
  reportLink: 'Go to Report Downloads'
};

/*
  The governed report journey's two terminal states (FR §4.13, PLA-9563 §5). The success copy is
  Desktop-14's `5032:11665` / `5032:11667` with the frame's own "Customer Engagement" dropped: the
  turn carries a `report_download_id` and no report NAME, and naming the report after the question
  would be an invention. v3 draws no failure frame, so that pair is the success pair's opposite in
  the same shape.
*/
const report = {
  successTop: '↗ Your requested report is generated successfully.',
  successBody: 'Download or send the generated report via email from the Report module.',
  failedTop: '⚠ The requested report could not be generated.',
  failedBody: 'Nothing was produced. The Report module lists the attempt and its status.'
};

// Analysis-card head. The emoji are part of the drawn strings (`4991:434`, `4952:8538`,
// `4952:8091`), as the session bullet is in `notice.activeSession`.
const analysis = {
  governed: '📈 Governed Analysis',
  // The chart's two controls (`5032:10910`, `5032:10912`) are drawn as bare icons. These are their
  // accessible names, not copy the design supplies.
  download: 'Download this chart',
  expand: 'Expand this chart'
};

/*
  div.access-card `4952:6262`-`4952:6270`. The card is drawn under a `permission_restricted` turn
  and its body is the server's own `answer.restrictions`; `body` here is only the fallback for a
  turn that states no reason. PLA-9579: the drawn title and body both asserted that permissions had
  CHANGED, which the payload never says - a request refused by a permission the reader never held
  reads exactly the same on the wire.
*/
const access = {
  title: 'Permission Restricted',
  body: 'Orbit can’t show this result under your current data permissions.',
  policyLink: '⌾ View Policy Details'
};

const actions = {
  ask: 'Ask Orbit',
  newChat: 'New Chat',
  conversations: 'Conversations',
  clear: 'Clear conversation',
  clearConfirm: 'Clear this conversation? Its messages will be removed and can’t be recovered.',
  clearConfirmTitle: 'Clear conversation?',
  clearConfirmOk: 'Clear',
  cancel: 'Cancel',
  close: 'Close',
  // `5118:16662` draws a minimise bar beside the close glyph. The panel has one collapsed
  // state - the floating launcher - so minimising and closing land in the same place.
  // `5118:16676` is the save glyph. PLA-8839 exposes only /v4/dash/orbit/requests and
  // /v4/dash/orbit/sessions, so it has no endpoint yet.
  save: 'Save investigation',
  saveUnavailable:
    'Saving an investigation is not available yet: it needs a backend service that has not been built.',
  // `5118:16678` opens the conversation list, the same one "View all history" opens
  // (user, 2026-09-15); the layer's `Save chat` name is the designer's library name for the glyph.
  history: 'History',
  retry: 'Retry',
  // BUTTON `5032:10922`. Everything below it is ours: the frames draw no disabled, sending or
  // help state for this control (figma-spec-v3.md § AMBER, "UI Specification Pending").
  growthPlanner: 'Send to Growth Planner',
  growthPlannerSending: 'Sending this result to the Growth Planner...',
  // PLA-9579: the restriction belongs to THIS answer, not to the conversation - the next question
  // is asked and sent normally.
  growthPlannerRestricted:
    'This answer is permission restricted, so it cannot be sent to the Growth Planner.',
  /*
    FR §4.14, condensed to what a reader needs at the button: what is transferred, why the button is
    only sometimes there, and where the planning actually happens. The FR bars dynamic values,
    formulas and confidence methodology from explainer copy, so none appears here.
  */
  growthPlannerHelp:
    'Sends only the minimum governed context of this result — the metric and the population behind it — to the Growth Planner. It appears only where every applicable subscription, permission, consent, policy, confidence, evidence, freshness and suppression gate has passed. Growth Planner validates the context independently; planning and execution happen there, not in Orbit.',
  growthPlannerHelpLabel: 'About Send to Growth Planner',
  loadMore: 'Load more',
  loadEarlier: 'Load earlier messages'
};

const notice = {
  activeSession: '● Active session',
  noConversations: 'No conversations yet.',
  analysing: 'Orbit is analyzing...',
  loadFailed: 'Orbit couldn’t load this conversation.',
  renderFailed: 'Something went wrong inside Orbit.',
  clearFailed: 'The conversation could not be cleared. Please try again.',
  conversationsLoadFailed: 'Orbit couldn’t load your conversations.',
  disclaimer: 'Orbit AI can make mistakes. Verify critical information.',
  /*
    One line per cause the composer can tell apart (PLA-9563 §8). `askFailed` stayed the catch-all
    for the failures that carry their own backend message (403 / 404 / 422) and for anything
    unclassified; the three below are the causes where the backend either says nothing or says
    something the reader cannot act on, and where the action to take differs.
  */
  askFailed: 'Orbit couldn’t send your question. Please try again.',
  askFailedNetwork: 'Orbit couldn’t reach the server. Check your connection and try again.',
  askFailedServer: 'Orbit is temporarily unavailable. Please try again in a moment.',
  askFailedSession: 'Your session has expired. Sign in again to continue.',
  sessionEnded: 'This conversation is no longer available. Start a new chat to continue.'
};

/*
  The save-outcome bubbles, from `5143:1490` and `5140:1483`, with the investigation's own subject in
  place of the frames' "Customer Engagement". The warning sign is inside the drawn string on the
  failure; the success tick is a separate node and is passed to the bubble as a glyph. `dismiss` is
  ours - the frames draw the bubble with no control on it.

  `newInvestigation` replaces the frames' `5140:1486` "is not saved. New investigation started."
  (user ruling, 2026-09-14): the backend retains every conversation - `GET /v4/dash/orbit/requests`
  lists any past session and the panel's Recent Sessions block reopens it - so the drawn sentence
  states a loss that never happens. It keeps the frame's second half and is drawn in the SUCCESS
  variant (`5032:11743`) instead.
*/
const toast = {
  saved: '{{subject}} investigation is saved successfully.',
  saveFailed: '⚠ {{subject}} investigation is not saved due to network issue.',
  newInvestigation:
    '{{subject}} investigation saved to Recent Sessions. New investigation started.',
  dismiss: 'Dismiss this message'
};

// Label card of the floating launcher (`4952:9806`, `4952:9805`). The headline reuses the module
// title; the frame's subtitle is "Insights: Lorem Ipsum" placeholder copy.
const launcher = {
  // `5017:6360` draws "Insights: Lorem Ipsum". The placeholder stands in for the live session
  // context (user, 2026-09-11), so the value is interpolated and the line is dropped entirely when
  // nothing resolves rather than rendered as a bare "Insights:".
  hint: 'Insights: {{context}}'
};

const thread = {
  you: 'You',
  orbit: 'Orbit AI',
  meta: '{{who}} · {{time}}',
  // div.sysNote `4952:7116` and div.msgTag `4952:7133`.
  sysNote: '✦ Investigation started automatically — no need to click "New Investigation"',
  continuation: '↳ continuing this investigation · no new context needed'
};

// div.handoffCard `4952:7148`. The frame names a customer ("Priya Sharma") the wire does not carry;
// the context label stands in its place.
const handoff = {
  opened: '↗ Opened {{context}}',
  body: 'Screen changed, but this is still the same investigation.',
  authorization: 'Fresh {{screen}} authorization performed',
  // Used when the route resolves to no screen Orbit has a label for.
  authorizationGeneric: 'Fresh authorization performed',
  carried: 'Permitted investigation context carried forward',
  /*
    The handoff's own outcomes (PLA-9563 §4b), drawn in the same card. TODO(AF-1): `sentTop` states
    that the request was formed and accepted, not that the Growth Planner holds it — there is no
    handoff endpoint yet, so anything stronger would be a claim the seam cannot support.
  */
  sentTop: '↗ Sent to the Growth Planner.',
  sentBody: 'Growth Planner validates the transferred context before it plans anything with it.',
  sentMetrics_one: '{{count}} governed metric transferred',
  sentMetrics_other: '{{count}} governed metrics transferred',
  // `count` selects the plural; `total` carries the same figure grouped the way every other
  // reading in the panel is grouped, which raw `{{count}}` interpolation does not do.
  sentCustomers_one: '{{total}} customer identified',
  sentCustomers_other: '{{total}} customers identified',
  // PLA-9579: a band whose `n_customers` is null is left OUT of the population rather than added as
  // 0, so the figure above can be an undercount and has to say so.
  sentPartial: 'Some periods stated no cohort size, so this population is partial',
  sentReference: 'Reference {{reference}}',
  blockedTop: '⚠ This result cannot be sent to the Growth Planner.',
  blockedBody: 'A governance gate on this result no longer passes, so nothing was sent.',
  unavailableTop: '⚠ The Growth Planner handoff did not complete.',
  unavailableBody: 'Nothing was sent. Try again from this answer. Reference {{reference}}.'
};

// Step rows of the processing card (figma-spec-v2.md `4952:6682`), in the order they complete.
// `retrieving` carries no `detail`: the frame draws a subtitle on the two ticked rows and leaves
// the row still running title-only (`4952:6695`), and that step never reaches `done` here.
const progress = {
  /*
    `5017:7862` is the bare sentence. The glyph in front of it is the Orbit mark (`5199:9958`), a
    drawn vector rather than a character, so the card renders it as a component and the string
    carries no sparkle.
  */
  analysing: 'Orbit is analyzing...',
  authorizing: {
    title: 'Authorizing access...',
    detail: 'Verified user credentials against compliance protocols.'
  },
  resolving: {
    title: 'Resolving governed query...',
    detail: 'Mapped semantic intent to secure data models.'
  },
  retrieving: {
    title: 'Retrieving and analyzing data...'
  },
  console: 'Executing secure fetch across data nodes...'
};

const metrics = {
  // Donut centre caption `4952:8545`; CSS uppercases it.
  total: 'Total',
  /*
    div.driver-title `4952:8096` reads "Growth Drivers: Retail Segment". The title slot now carries
    the PLOTTED METRIC's own name, because the PNG export rasterises the bar box and this constant
    was labelling a Lifecycle Stage Distribution "GROWTH DRIVERS" (PLA-9563 §6a). It survives as the
    fallback for a metric the payload named with an empty string.
  */
  drivers: 'Growth Drivers',
  columnMetric: 'Metric',
  columnValue: 'Value',
  columnKey: 'Breakdown',
  columnCustomers: 'Customers',
  showBreakdown_one: 'Show the {{count}}-point breakdown',
  showBreakdown_other: 'Show the {{count}}-point breakdown',
  noValue: '—',
  noData: 'No data returned',
  reasonCodes: 'Reason codes',
  customers_one: '{{count}} customer',
  customers_other: '{{count}} customers'
};

// The customer group a cohort metric was answered for. Dimension names follow the Customer Insights
// filter and distribution copy; band values resolve through `CustomerInsights.Filters.Categories`.
const cohort = {
  filtersLabel: 'Customer group filters',
  activityWindow_one: 'Active in last {{count}} day',
  activityWindow_other: 'Active in last {{count}} days',
  tag: '{{dimension}}: {{values}}',
  dimensions: {
    lifecycle_stage_in: 'Lifecycle stage',
    lifecycle_stage_not_in: 'Lifecycle stage excluded',
    current_clv_band_in: 'Current CLV band',
    predicted_clv_band_in: 'Predicted CLV band',
    holding_count_band_in: 'Product holding count',
    product_relationship_in: 'Product relationship',
    disengagement_risk_band_in: 'Disengagement risk'
  },
  population: 'Based on {{customers}} customers',
  dataAsOf: 'Data as of {{date}}',
  selected: 'Selected: {{bands}}',
  selectedCustomers_one: '{{total}} customer',
  selectedCustomers_other: '{{total}} customers'
};

// FR "List and Table Pagination". `Show more` grows the page client-side: the contract delivers the
// whole series inline and has no paging endpoint to round-trip to.
const paging = {
  showing: 'Showing 1-{{shown}} of {{total}}',
  showMore: 'Show more',
  capped: 'Generate a report for the full set.'
};

// Copy from figma-spec.md `4553:3964`. The chat list carries no message count, so the meta line is
// `lastActive` alone.
const conversation = {
  heading: 'Saved Investigations',
  back: 'Back to conversation',
  lastActive: 'Last active {{when}}'
};

// Full-page Orbit. `newChat` also titles a chat that has no first question on record yet.
const page = {
  // FR §4.12 global prompt starters, verbatim.
  starters: {
    changed: 'What changed?',
    attention: 'What needs my attention?',
    trend: 'Show me the trend.',
    why: 'Why is this happening?',
    next: 'What can I do next?'
  },
  newChat: 'New chat',
  // The "…" menu on a rail row, the chat header and the side panel's chat-name bar (Figma
  // 6519:8247 / 6519:8283 / 6690:3471).
  chatMenu: {
    label: 'Chat options',
    rename: 'Rename',
    pin: 'Pin',
    unpin: 'Unpin',
    delete: 'Delete',
    addToWorkspace: 'Add to Workspace',
    moveToWorkspace: 'Move to Workspace',
    removeFromWorkspace: 'Remove from Workspace'
  },
  // Figma 6703:5733: the open chat's Workspace, under its title.
  addedWorkspace: 'Added Workspace: {{name}}',
  // The composer's Workspace control (Figma 6454:11452).
  workspacePicker: {
    label: 'Choose a Workspace',
    group: 'Workspaces',
    none: 'No Workspace',
    chip: 'Workspace: {{name}}'
  },
  pinnedEmpty: 'Pin a chat to keep it here.',
  search: {
    open: 'Search chats',
    close: 'Close search',
    placeholder: 'Search conversations...',
    results: 'Search results',
    empty: 'No matching conversations found.'
  },
  // Figma 6454:8168: a Workspace's chats, out of its rail row.
  workspaceFlyout: {
    label: '{{name}} chats',
    empty: 'No chats in this Workspace yet',
    failed: 'Couldn’t load these chats.'
  },
  renameChat: {
    title: 'Rename chat',
    label: 'Chat name',
    hint: 'Leave it empty to name the chat by its first question again.',
    save: 'Save',
    invalid: 'Use 1 to 120 characters.'
  },
  chatUpdate: {
    failed: 'The chat could not be updated. Please try again.',
    gone: 'This chat no longer exists.',
    fileFailed: 'The chat could not be saved in {{name}}. You can add it from its menu.'
  },
  deleteChat: {
    title: 'Delete chat?',
    body: "This removes '{{title}}' from your history, Workspaces, Pinned and search. You can't restore it.",
    confirm: 'Delete',
    failed: 'The chat could not be deleted. Please try again.'
  }
};

const support = {
  notSubscribed: 'Not subscribed',
  answerNumber: 'ANSWER {{index}} OF {{total}}',
  ticket: {
    environment: 'Environment',
    domain: 'Domain',
    area: 'Area',
    tenant: 'Tenant',
    issueType: 'Issue type / Category',
    select: 'Select…',
    detectedIssue: 'Detected issue',
    description: 'Issue description',
    evidence: 'Evidence',
    submitting: 'Submitting…',
    created: 'Ticket created in Zendesk.',
    createdDetail: 'Ticket {{id}} · Status: {{status}}',
    failed: 'The ticket was not created.'
  },
  contract: {
    value: 'Contract value',
    duration: 'Duration',
    range: '{{start}} – {{end}}'
  },
  weekly: {
    heading: 'Weekly Update',
    weekOf: 'Week of {{date}}',
    reportingDate: 'Reporting Date',
    projectManager: 'Project Manager',
    summary: 'Summary',
    highlights: 'Strategic Highlights',
    resolvedIssues: 'Resolved Issues',
    openWatchItems: 'Open Watch Items'
  },
  incidents: {
    heading: 'Active Incidents',
    openCount: '{{count}} Open',
    id: 'Incident ID',
    title: 'Title',
    severity: 'Severity',
    status: 'Status',
    opened: 'Opened'
  },
  maintenance: {
    heading: 'Planned Maintenance',
    upcomingCount: '{{count}} Upcoming',
    id: 'Maintenance ID',
    title: 'Title',
    start: 'Start ({{zone}})',
    end: 'End ({{zone}})'
  },
  tickets: {
    heading: 'Active Support Tickets ({{count}})',
    id: 'Ticket ID',
    title: 'Title',
    severity: 'Severity',
    status: 'Status',
    opened: 'Opened',
    created: 'Created',
    lastUpdated: 'Last Updated',
    description: 'Description',
    jira: 'Jira',
    jiraPending: 'Pending / not yet linked'
  },
  status: {
    open: 'Open',
    pending: 'Pending',
    investigating: 'Investigating',
    resolved: 'Resolved',
    closed: 'Closed'
  },
  renewal: {
    heading: 'Renewal Review Request',
    tenant: 'Tenant',
    contractEnd: 'Current Contract End',
    accountValue: 'Account Value',
    reviewWindow: 'Preferred Review Window',
    notes: 'Additional Notes (Optional)',
    submitting: 'Submitting…',
    submitted: 'Renewal review requested.',
    submittedBody: 'The renewal-review service confirmed the request.',
    failed: 'The renewal review was not requested.'
  },
  // FR §4.24 governed states and §4.25 freshness.
  states: {
    asOf: 'Data as of {{date}}',
    period: 'Period: {{period}}',
    refresh: 'Refresh with latest data',
    permission_restricted: {
      title: 'Restricted',
      body: 'You do not have access to this information.'
    },
    valid_zero: {
      title: 'Zero is the actual value',
      body: 'The source reported zero for this period.'
    },
    not_configured: {
      title: 'Not configured',
      body: 'This is not set up for your tenant.'
    },
    insufficient_data: {
      title: 'Insufficient data',
      body: 'There is not enough data to answer this yet.'
    },
    not_available: {
      title: 'Not available',
      body: 'The source has no value for this field.'
    },
    upstream_error: {
      title: 'Unavailable',
      body: 'The source could not be reached. Try again later.'
    },
    stale: {
      title: 'Stale data',
      body: 'This data may be out of date.'
    },
    entitlement_unavailable: {
      title: 'Entitlement unavailable',
      body: 'Subscription status could not be confirmed right now. This does not mean the capability is not subscribed.'
    }
  }
};

const layer4 = {
  confirm: 'Confirm',
  cancel: 'Cancel',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  record: {
    rule: 'Rule',
    campaign: 'Campaign',
    audience: 'Audience',
    reward: 'Reward',
    loyalty_program: 'Programme',
    fallback: 'Record'
  },
  expires: 'Expires',
  expiresAt: 'Expires at {{time}}',
  recordValue: '{{name}} (#{{id}})',
  working: {
    deactivate_transaction_rule: 'Deactivating Rule {{name}} (#{{id}})...',
    activate_transaction_rule: 'Activating Rule {{name}} (#{{id}})...',
    fallback: 'Working on {{name}}...'
  },
  expired: {
    clock: 'This proposal expired. Ask again.',
    changed: 'This proposal changed or expired. Ask again.'
  },
  failed: 'That did not go through. Try again.',
  picker: {
    id: 'Rule ID',
    name: 'Name',
    state: 'Status'
  },
  pendingBadge: 'Pending approval',
  handedOff: 'Created in builder (#{{id}})',
  savedInBuilder: 'Saved in the builder (#{{id}}).',
  handoffConflict:
    'Saved, but this proposal was already closed or expired, so Orbit did not record it.',
  handoffFailed: 'Saved, but Orbit could not record it. The record is linked on the card.',
  outOfScope: 'Out of scope',
  link: {
    rule: 'View rule',
    audience: 'View audience',
    campaign: 'View campaign',
    reward: 'View reward',
    loyalty_program: 'View loyalty programme'
  },
  demo: {
    title: 'Demo: Layer 4 flows',
    intro:
      'This demo chat replays the Layer 4 side-panel flows with local data. Nothing is sent to Perx. Pick a flow below.',
    showFlows: 'Demo flows ({{count}})',
    hideFlows: 'Hide demo flows'
  }
};

const builder = {
  unavailable: 'This builder cannot be opened here.',
  blocked: 'Finish or leave the open {{module}} editor first.',
  failed: 'The builder could not be loaded.',
  module: {
    campaign: 'campaign',
    reward: 'reward'
  }
};

export const translations = {
  [AssistantKey]: {
    title: 'Orbit',
    starters,
    home,
    idle,
    context,
    conflict,
    composer,
    outcome,
    answer,
    report,
    analysis,
    access,
    actions,
    notice,
    toast,
    launcher,
    thread,
    handoff,
    progress,
    metrics,
    cohort,
    paging,
    conversation,
    support,
    page,
    wizard,
    configStudio,
    layer4,
    builder
  }
};
